/**
 * Side Panel — Shows compact user profiles in the right sidebar.
 * Used on search (replaces ads sidebar) and messages (replaces ads column).
 * Renders content directly into [data-side-panel-target] to scroll with the page layout.
 *
 * User preference (persisted in localStorage as `side_panel_disabled`) lets users
 * hide the compact profile panel globally. When disabled, open() is a no-op except
 * it ensures a small "Show profile" restore button is visible at the top of the
 * sidebar so they can turn the panel back on.
 */
(function() {
    'use strict';

    var PREF_KEY = 'side_panel_disabled';
    var RESTORE_BTN_ID = 'side-panel-restore-btn';

    var _isOpen = false;
    var _currentUserId = null;
    var _originalContent = null;
    var _originalStyle = null;
    var _targetEl = null;
    var _fetchController = null;
    var _lastRequest = null; // { userId, context, clickSource } — last user asked to open, for re-enable

    function isDisabled() {
        try {
            return localStorage.getItem(PREF_KEY) === '1';
        } catch (err) {
            return false;
        }
    }

    function setDisabled(disabled) {
        try {
            if (disabled) {
                localStorage.setItem(PREF_KEY, '1');
            } else {
                localStorage.removeItem(PREF_KEY);
            }
        } catch (err) {}
    }

    function getTarget() {
        return document.querySelector('[data-side-panel-target]');
    }

    function isTargetVisible(el) {
        return el && el.offsetWidth > 0;
    }

    function initIcons(container) {
        if (window.LucideIcons) {
            window.LucideIcons.initialize(container);
        } else if (window.lucide) {
            window.lucide.createIcons({ parent: container });
        }
    }

    function initAlpine(container) {
        if (window.Alpine && window.Alpine.initTree) {
            window.Alpine.initTree(container);
        }
    }

    // Scripts inserted via innerHTML are inert per the HTML5 spec. Replace each
    // <script> with a freshly-created element so the browser executes it —
    // required for deferred-ad activators and any other inline scripts.
    function executeScripts(container) {
        var scripts = container.querySelectorAll('script');
        for (var i = 0; i < scripts.length; i++) {
            var orig = scripts[i];
            var s = document.createElement('script');
            for (var j = 0; j < orig.attributes.length; j++) {
                var attr = orig.attributes[j];
                s.setAttribute(attr.name, attr.value);
            }
            if (!orig.src) s.textContent = orig.textContent;
            orig.parentNode.replaceChild(s, orig);
        }
    }

    function setContent(target, html, animate) {
        if (!animate) {
            target.innerHTML = html;
            executeScripts(target);
            return;
        }
        target.style.opacity = '0';
        target.innerHTML = html;
        executeScripts(target);
        // Force reflow then fade in
        void target.offsetHeight;
        target.style.transition = 'opacity 150ms ease-in';
        target.style.opacity = '1';
    }

    function showLoading(target) {
        setContent(target, '<div class="flex flex-col items-center justify-center h-64 gap-3">' +
            '<div class="w-6 h-6 border-2 border-gray-300 border-t-sky-500 rounded-full animate-spin"></div>' +
            '<span class="text-xs text-gray-400">' + (window.trans ? window.trans('common', 'loading') : 'Loading...') + '</span>' +
            '</div>', false);
    }

    function showError(target, userId, options) {
        target.innerHTML = '<div class="flex flex-col items-center justify-center h-64 gap-3 px-4 text-center">' +
            '<i data-lucide="alert-circle" class="w-8 h-8 text-gray-300"></i>' +
            '<p class="text-sm text-gray-500">' + (window.trans ? window.trans('common', 'error_occurred') : 'Something went wrong') + '</p>' +
            '<button type="button" class="text-sm font-medium text-sky-600 hover:text-sky-700" ' +
            'onclick="window.SidePanel._retry(\'' + userId + '\')">' +
            (window.trans ? window.trans('common', 'try_again') : 'Try again') +
            '</button>' +
            '</div>';
        initIcons(target);
    }

    // Insert a small "Show profile" button at the top of the sidebar's original
    // content, so when the panel is disabled the user still has a visible way
    // back. The button lives inside whatever the original side-panel target
    // contains (ads), not replacing anything.
    function ensureRestoreButton() {
        var target = getTarget();
        if (!target) return;
        if (document.getElementById(RESTORE_BTN_ID)) return;
        var label = window.trans ? window.trans('common', 'show_profile_panel') : 'Show profile';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.id = RESTORE_BTN_ID;
        btn.className = 'w-full flex items-center justify-center gap-2 text-xs text-gray-500 hover:text-sky-600 hover:bg-sky-50 border border-gray-200 hover:border-sky-200 rounded-full py-2 px-3 transition-colors';
        btn.innerHTML = '<i data-lucide="panel-right-open" class="w-3.5 h-3.5"></i><span>' + label + '</span>';
        btn.addEventListener('click', function() { window.SidePanel.enable(); });
        target.insertBefore(btn, target.firstChild);
        initIcons(target);
    }

    function removeRestoreButton() {
        var btn = document.getElementById(RESTORE_BTN_ID);
        if (btn && btn.parentNode) btn.parentNode.removeChild(btn);
    }

    function open(userId, options) {
        options = options || {};
        var clickSource = options.clickSource || 0;

        var target = getTarget();
        if (!target || !isTargetVisible(target)) return;

        userId = String(userId);
        var context = options.context || 'search';
        _lastRequest = { userId: userId, context: context, clickSource: clickSource };

        // Respect user preference — when disabled, don't load the panel but
        // surface a restore button on the sidebar so they can turn it back on.
        if (isDisabled()) {
            ensureRestoreButton();
            return;
        }

        var cacheKey = userId + ':' + context;

        // Same user already displayed — skip refetch
        if (_isOpen && _currentUserId === cacheKey) return;

        // Save original content and styles on first open
        if (!_isOpen && _originalContent === null) {
            _originalContent = target.innerHTML;
            _originalStyle = target.getAttribute('style') || '';
            _targetEl = target;
            // Remove padding/margin classes and make target a sticky scroll container
            target.classList.remove('px-4', 'pt-4', 'space-y-6');
            target.style.position = 'sticky';
            target.style.top = '0';
            target.style.maxHeight = '100vh';
            target.style.overflowY = 'auto';
            target.style.overflowX = 'hidden';
        }

        // Cancel any in-flight fetch
        if (_fetchController) {
            _fetchController.abort();
            _fetchController = null;
        }

        _isOpen = true;
        _currentUserId = cacheKey;

        // Show loading state
        showLoading(target);

        // Build URL — the backend decides whether to log the view based on context
        // (throttled within a short window) so we always hit the endpoint rather
        // than serving from cache.
        var url = '/app/profile/compact?uid=' + encodeURIComponent(userId) +
            '&_cs=' + encodeURIComponent(clickSource) +
            '&context=' + encodeURIComponent(context);

        // Fetch
        _fetchController = new AbortController();
        fetch(url, { signal: _fetchController.signal, credentials: 'same-origin' })
            .then(function(resp) {
                if (!resp.ok) throw new Error('HTTP ' + resp.status);
                return resp.text();
            })
            .then(function(html) {
                _fetchController = null;
                if (_currentUserId !== cacheKey) return; // Stale response

                if (!html || html.trim().length === 0) {
                    showError(target, userId, options);
                    return;
                }

                setContent(target, html, true);
                initIcons(target);
                initAlpine(target);
                target.scrollTop = 0;
            })
            .catch(function(err) {
                _fetchController = null;
                if (err.name === 'AbortError') return;
                if (_currentUserId !== cacheKey) return;
                showError(target, userId, options);
            });
    }

    function close() {
        if (!_isOpen) return;

        // Restore original sidebar content and styles
        if (_targetEl && _originalContent !== null) {
            _targetEl.innerHTML = _originalContent;
            _targetEl.setAttribute('style', _originalStyle || '');
            // Restore padding/margin classes
            _targetEl.classList.add('px-4', 'pt-4', 'space-y-6');
        }

        _isOpen = false;
        _currentUserId = null;
    }

    // User hid the compact profile pane. Close the current render, persist the
    // preference, and show a small restore button on the sidebar.
    function disable() {
        setDisabled(true);
        close();
        ensureRestoreButton();
    }

    // User wants the panel back. Clear preference and reopen the most recently
    // requested profile if we have one.
    function enable() {
        setDisabled(false);
        removeRestoreButton();
        if (_lastRequest) {
            open(_lastRequest.userId, {
                clickSource: _lastRequest.clickSource,
                context: _lastRequest.context
            });
        }
    }

    window.SidePanel = {
        open: open,
        close: close,
        disable: disable,
        enable: enable,
        isOpen: function() { return _isOpen; },
        isDisabled: isDisabled,
        currentUserId: function() { return _currentUserId; },
        // Internal retry handler
        _retry: function(userId) {
            _currentUserId = null; // Force refetch
            open(userId, { clickSource: 0 });
        }
    };

    // On page load, surface the restore button if the panel is disabled and a
    // target sidebar exists, so the user always has a visible way to turn it
    // back on — even before they click anything.
    function initOnLoad() {
        if (!isDisabled()) return;
        var target = getTarget();
        if (!target || !isTargetVisible(target)) return;
        ensureRestoreButton();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initOnLoad);
    } else {
        initOnLoad();
    }
})();
